import { memo, useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import {
    ColorType,
    createChart,
    type IChartApi,
    type Time,
    type UTCTimestamp,
} from 'lightweight-charts';

import { THEME } from '@/shared/constants/theme';
import {
    useDesktopMediaQuery,
    useLanguage,
    useTheme,
} from '@/shared/lib/hooks';

import type { CalendarData } from '../../models/types/transformed-calendar-data';

interface TradingPlatformDailyInfoChartProps {
    data: CalendarData['chartData'];
    isInsideDrawer?: boolean;
}

export const TradingPlatformDailyInfoChart = memo(
    (props: TradingPlatformDailyInfoChartProps) => {
        const { data, isInsideDrawer = false } = props;

        const chartContainerRef = useRef<HTMLDivElement | null>(null);
        const chart = useRef<IChartApi | null>(null);

        const [theme] = useTheme();
        const [language] = useLanguage();

        const isDesktop = useDesktopMediaQuery();

        const currentDarkBackground = useMemo(
            () => (isInsideDrawer ? '#131722' : 'transparent'),
            [isInsideDrawer],
        );

        useEffect(() => {
            chart.current = createChart(chartContainerRef.current!, {
                layout: {
                    fontFamily: "'Roboto', sans-serif",
                },
                ...(isInsideDrawer ? {} : { width: isDesktop ? 300 : 266 }),
                height: 200,
                autoSize: isInsideDrawer,
                crosshair: {
                    horzLine: { visible: false },
                    vertLine: { visible: false },
                },
                timeScale: {
                    timeVisible: true,
                    ticksVisible: true,
                    uniformDistribution: true,
                    tickMarkFormatter: (time: Time) =>
                        dayjs(time as UTCTimestamp).format('HH:mm:ss'),
                },
                grid: {
                    vertLines: {
                        visible: false,
                    },
                },
                handleScroll: true,
                handleScale: true,
                leftPriceScale: {
                    visible: true,
                    alignLabels: false,
                },
                rightPriceScale: {
                    visible: false,
                },
            });

            chart.current.timeScale().fitContent();

            const chartData = () => {
                let sum: number = 0;

                return data.map(({ time, value }) => {
                    sum += value;

                    return { time, value: sum };
                });
            };

            const accumulatedData = chartData();

            const firstItem = accumulatedData[0];
            const isDataStartFromZero = firstItem && !firstItem.value;

            if (firstItem && !isDataStartFromZero) {
                accumulatedData.unshift({
                    time: dayjs(firstItem.time)
                        .startOf('day')
                        .valueOf() as UTCTimestamp,
                    value: 0,
                });
            }

            const baselineSeries = chart.current.addBaselineSeries({
                baseValue: { type: 'price', price: 0 },
                topLineColor: 'rgba( 38, 166, 154, 1)',
                topFillColor1: 'rgba( 38, 166, 154, 0.28)',
                topFillColor2: 'rgba( 38, 166, 154, 0.05)',
                bottomLineColor: 'rgba( 239, 83, 80, 1)',
                bottomFillColor1: 'rgba( 239, 83, 80, 0.05)',
                bottomFillColor2: 'rgba( 239, 83, 80, 0.28)',
                priceLineVisible: false,
                lastValueVisible: false,
                lineWidth: 1,
            });

            baselineSeries.setData(accumulatedData);

            return () => {
                chart.current?.remove();
            };
        }, [data, isDesktop, isInsideDrawer]);

        useEffect(() => {
            const isDark = theme === THEME.dark;

            chart.current?.applyOptions({
                layout: {
                    background: {
                        type: ColorType.Solid,
                        color: isDark ? currentDarkBackground : 'transparent',
                    },
                    textColor: isDark
                        ? 'rgb(255 255 255 / 45%)'
                        : 'rgb(0 0 0 / 45%)',
                },
                grid: {
                    horzLines: {
                        color: isDark ? '#2A2E39' : '#F0F0F0',
                    },
                },
                leftPriceScale: {
                    borderColor: isDark ? '#2A2E39' : '#F0F0F0',
                },
                timeScale: {
                    borderColor: isDark ? '#2A2E39' : '#F0F0F0',
                },
                localization: {
                    timeFormatter: (time: UTCTimestamp) =>
                        dayjs(time).format('HH:mm:ss'),
                },
            });
        }, [currentDarkBackground, theme]);

        useEffect(() => {
            chart.current?.applyOptions({
                localization: {
                    locale: language,
                },
            });
        }, [language]);

        return <div ref={chartContainerRef} />;
    },
);

TradingPlatformDailyInfoChart.displayName = 'TradingPlatformDailyInfoChart';
